<script setup>
import { Head, usePage } from '@inertiajs/vue3';
import { computed } from 'vue';

const page = usePage();
const dir = computed(() => page.props.locale === 'ar' ? 'rtl' : 'ltr');

const factoryInfo = {
    titleKey: 'factory_blanco_jordan',
    descKey: 'factory_blanco_jordan_desc',
    image: '/assets/factories/blanco-jordan.png',
    startYear: '1998',
    certificates: 'ISO9001',
    location: 'factory_location_jordan',
    productsCount: '23',
    phone: '+96265816008',
    fax: '+96265814008',
    email: 'blanco-jo@shawaf-group.com',
    address: 'factory_address_jordan',
    website: 'https://blankokimya.com/'
};
</script>

<template>

    <Head :title="__t('factory_blanco_jordan')" />

    <div class="bg-white dark:bg-slate-900 min-h-screen font-sans" :dir="dir">

        <!-- Hero Section -->
        <section
            class="relative bg-gradient-to-br from-primary/10 pt-[200px] via-white to-primary/5 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800 py-20 overflow-hidden">
            <!-- Decorative Wave -->


            <div class="container mx-auto px-6 relative z-10">
                <h1
                    class="text-4xl md:text-5xl font-bold text-center text-slate-900 dark:text-white animate-fade-in-up">
                    {{ __t(factoryInfo.titleKey) }}
                </h1>
            </div>
        </section>

        <!-- Main Content -->
        <section class="py-16 md:py-24">
            <div class="container mx-auto px-6">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">

                    <!-- Left Column - Description & Image -->
                    <div class="space-y-8 animate-fade-in-left" data-aos="fade-right">
                        <div
                            class="bg-gradient-to-br from-primary/5 to-transparent dark:from-primary/10 p-8 rounded-2xl">
                            <h2 class="text-3xl font-bold text-slate-900 dark:text-white mb-6 relative inline-block">
                                {{ __t(factoryInfo.titleKey) }}
                                <span class="absolute -bottom-2 start-0 w-1/3 h-1 bg-primary rounded-full"></span>
                            </h2>
                            <p class="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                                {{ __t(factoryInfo.descKey) }}
                            </p>
                        </div>

                        <div class="relative group">
                            <div
                                class="absolute -inset-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-3xl transform -rotate-2 group-hover:-rotate-3 transition-transform duration-500">
                            </div>
                            <div class="relative overflow-hidden rounded-2xl shadow-2xl">
                                <img :src="factoryInfo.image" :alt="__t(factoryInfo.titleKey)"
                                    class="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-700">
                            </div>
                        </div>

                        <!-- Visit Website Button -->
                        <div class="flex justify-start" data-aos="fade-in" data-aos-delay="400">
                            <a :href="factoryInfo.website" target="_blank" rel="noopener noreferrer"
                                class="inline-flex items-center gap-2 px-8 py-3 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
                                <span>{{ __t('visit_website') }}</span>
                                <i class="fas fa-external-link-alt"></i>
                            </a>
                        </div>
                    </div>

                    <!-- Right Column - Info Cards -->
                    <div class="space-y-6 animate-fade-in-right" data-aos="fade-left">

                        <!-- Info Cards Grid -->
                        <div class="grid grid-cols-2 gap-4">
                            <!-- Start Year -->
                            <div
                                class="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-6 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <div class="flex items-center gap-4">
                                    <div class="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                                        <i class="far fa-calendar-alt text-white text-xl"></i>
                                    </div>
                                    <div>
                                        <h3 class="text-sm font-semibold text-gray-600 dark:text-gray-400">{{
                                            __t('factory_start_year') }}</h3>
                                        <p class="text-2xl font-bold text-slate-900 dark:text-white">{{
                                            factoryInfo.startYear }}</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Certificates -->
                            <div
                                class="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 p-6 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <div class="flex items-center gap-4">
                                    <div class="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                                        <i class="far fa-star text-white text-xl"></i>
                                    </div>
                                    <div>
                                        <h3 class="text-sm font-semibold text-gray-600 dark:text-gray-400">{{
                                            __t('factory_certificates') }}</h3>
                                        <p class="text-xl font-bold text-slate-900 dark:text-white">{{
                                            factoryInfo.certificates }}</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Location -->
                            <div
                                class="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-6 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <div class="flex items-center gap-4">
                                    <div class="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                                        <i class="fas fa-map-marker-alt text-white text-xl"></i>
                                    </div>
                                    <div>
                                        <h3 class="text-sm font-semibold text-gray-600 dark:text-gray-400">{{
                                            __t('factory_location') }}</h3>
                                        <p class="text-xl font-bold text-slate-900 dark:text-white">{{
                                            __t(factoryInfo.location) }}</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Products Count -->
                            <div
                                class="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-6 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                                <div class="flex items-center gap-4">
                                    <div class="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                                        <i class="far fa-square text-white text-xl"></i>
                                    </div>
                                    <div>
                                        <h3 class="text-sm font-semibold text-gray-600 dark:text-gray-400">{{
                                            __t('factory_products_count') }}</h3>
                                        <p class="text-2xl font-bold text-slate-900 dark:text-white">{{
                                            factoryInfo.productsCount }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Contact Information -->
                        <div
                            class="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700 p-8 rounded-2xl mt-8">
                            <h3 class="text-2xl font-bold text-slate-900 dark:text-white mb-4 text-center">
                                {{ __t('factory_contact_info') }}
                            </h3>
                            <p class="text-center text-gray-600 dark:text-gray-400 mb-6">{{ __t('factory_contact_us') }}
                            </p>

                            <div
                                class="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent mb-6">
                            </div>

                            <ul class="space-y-4">
                                <li
                                    class="flex items-start gap-3 hover:bg-white dark:hover:bg-slate-600 p-3 rounded-lg transition-colors">
                                    <i class="fas fa-map-marker-alt text-primary text-lg mt-1"></i>
                                    <span class="text-gray-700 dark:text-gray-300">{{ __t(factoryInfo.address) }}</span>
                                </li>
                                <li
                                    class="flex items-start gap-3 hover:bg-white dark:hover:bg-slate-600 p-3 rounded-lg transition-colors">
                                    <i class="fas fa-phone text-primary text-lg mt-1"></i>
                                    <a :href="'tel:' + factoryInfo.phone"
                                        class="text-gray-700 dark:text-gray-300 hover:text-primary transition-colors">
                                        {{ __t('factory_phone') }}: {{ factoryInfo.phone }}
                                    </a>
                                </li>
                                <li
                                    class="flex items-start gap-3 hover:bg-white dark:hover:bg-slate-600 p-3 rounded-lg transition-colors">
                                    <i class="fas fa-fax text-primary text-lg mt-1"></i>
                                    <span class="text-gray-700 dark:text-gray-300">{{ __t('factory_fax') }}: {{
                                        factoryInfo.fax }}</span>
                                </li>
                                <li
                                    class="flex items-start gap-3 hover:bg-white dark:hover:bg-slate-600 p-3 rounded-lg transition-colors">
                                    <i class="fas fa-mail-bulk text-primary text-lg mt-1"></i>
                                    <a :href="'mailto:' + factoryInfo.email"
                                        class="text-gray-700 dark:text-gray-300 hover:text-primary transition-colors">
                                        {{ __t('factory_email') }}: {{ factoryInfo.email }}
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<style scoped>
.animate-fade-in-up {
    animation: fadeInUp 0.8s ease-out forwards;
    opacity: 0;
    transform: translateY(20px);
}

.animate-fade-in-left {
    animation: fadeInLeft 0.8s ease-out forwards;
    opacity: 0;
    transform: translateX(-20px);
}

.animate-fade-in-right {
    animation: fadeInRight 0.8s ease-out forwards;
    opacity: 0;
    transform: translateX(20px);
}

@keyframes fadeInUp {
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeInLeft {
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes fadeInRight {
    to {
        opacity: 1;
        transform: translateX(0);
    }
}
</style>
